import Reveal from "@/components/feature/Reveal";
import SectionHeading from "./SectionHeading";
import { testimonials } from "@/mocks/testimonials";

const principles = [
  {
    icon: "ri-user-forbid-line",
    title: "只做通道，不做判断",
    desc: "平台负责接收与核实线索，不对当事人作道德评判；事实由多方信源共同拼出，而不是由单一投稿决定。",
  },
  {
    icon: "ri-file-shield-2-line",
    title: "未核实，不作事实表述",
    desc: "凡是无法交叉验证的内容，我们只会以「线索正在核实」的方式呈现，绝不当作已经发生的事实来写。",
  },
  {
    icon: "ri-eye-off-line",
    title: "隐去一切可识别信息",
    desc: "投稿人的联系方式、称呼与可指向身份的描述，都会在发布前被处理掉，这是编辑部最基本的纪律。",
  },
];

export default function About() {
  return (
    <section id="about" className="w-full bg-background-50 py-16 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-4 md:px-8">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <div className="relative h-[300px] w-full overflow-hidden rounded-lg md:h-[440px]">
              <img
                src="https://readdy.ai/api/search-image?query=editorial%20desk%20scene%20with%20stack%20of%20handwritten%20notes%20and%20sealed%20envelopes%2C%20warm%20paper%20texture%2C%20soft%20window%20light%2C%20deep%20crimson%20accent%20details%2C%20muted%20brown%20and%20cream%20tones%2C%20quiet%20investigative%20mood%2C%20no%20text&width=900&height=1100&seq=guatian-about-desk&orientation=portrait"
                alt="编辑部桌面上堆放着匿名来信与手写笔记"
                className="h-full w-full object-cover object-top"
              />
            </div>
          </Reveal>

          <Reveal delay={100}>
            <div>
              <SectionHeading
                eyebrow="关于我们"
                title="一个只负责接收与核实的爆料编辑部"
                description="瓜田爆料由一支小型编辑团队维护，我们不生产故事，只把来自一线的线索整理清楚，再交给愿意了解真相的人。"
              />
              <p className="mt-5 text-sm leading-relaxed text-foreground-600 md:text-base">
                平台的存在只有一个理由：让掌握信息却不愿露面的人，有一个安全的地方把话说完。
                因此我们不设账号体系、不要求实名，投稿入口永远保持匿名与开放。
                对于每一条线索，编辑都会先问「能不能核实」，再问「要不要发布」。
              </p>

              <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
                {[
                  { value: "2019", label: "开始接收投稿" },
                  { value: "8", label: "长期征集分类" },
                  { value: "1.2万+", label: "累计投稿线索" },
                ].map((item) => (
                  <div key={item.label} className="rounded-lg border border-background-200 bg-background-100 p-4">
                    <span className="block font-heading text-xl font-bold text-primary-600 md:text-2xl">
                      {item.value}
                    </span>
                    <span className="mt-1 block text-xs text-foreground-600">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={80}>
          <h3 className="mt-16 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            我们坚持的三条内容原则
          </h3>
        </Reveal>
        <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-3">
          {principles.map((item, index) => (
            <Reveal key={item.title} delay={index * 80} className="h-full">
              <article className="flex h-full flex-col rounded-lg border border-background-200 bg-background-100 p-6 md:p-7">
                <span className="flex h-12 w-12 items-center justify-center rounded-md bg-accent-100 text-2xl text-accent-700">
                  <i className={item.icon} aria-hidden="true" />
                </span>
                <h4 className="mt-5 font-heading text-base font-bold text-foreground-950">
                  {item.title}
                </h4>
                <p className="mt-2.5 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={80}>
          <h3 className="mt-16 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            投稿人怎么说
          </h3>
        </Reveal>
        <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-3">
          {testimonials.map((item, index) => (
            <Reveal key={item.id} delay={index * 80} className="h-full">
              <figure className="flex h-full flex-col rounded-lg border border-background-200 bg-background-100 p-6 md:p-7">
                <div className="flex items-center gap-1 text-accent-500" aria-label={`评分 ${item.stars} 星`}>
                  {Array.from({ length: item.stars }).map((_, starIndex) => (
                    <i key={starIndex} className="ri-star-fill text-sm" aria-hidden="true" />
                  ))}
                </div>
                <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-foreground-700">
                  「{item.quote}」
                </blockquote>
                <figcaption className="mt-5 flex items-center gap-3 border-t border-background-200 pt-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-100 text-lg text-secondary-700">
                    <i className="ri-user-3-line" aria-hidden="true" />
                  </span>
                  <span>
                    <span className="block text-sm font-medium text-foreground-900">{item.nickname}</span>
                    <span className="block text-xs text-foreground-500">{item.role}</span>
                  </span>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}